// imageUtils.ts
import { ExifUtils } from './ExifUtils';

export const ImageUtils = {
  JPEG_COMPRESSION: 0.75,
  RESIZE_TYPE_LANCZOS_3: 3,
  ORIENTATION_TOP_LEFT: 1,
  TARGET_WIDTH: 2048,
  TARGET_HEIGHT: 1536,
};

export interface ImageConfig {
  maxWidth: number;
  maxHeight: number;
  quality?: number;
}

export interface ImageTargetSize {
  width: number;
  height: number;
}

export interface ResizeImageConfig {
  file: File;
  targetSize: ImageTargetSize;
}

export interface ImageExifData {
  width: number;
  height: number;
  exif: any;
}

export interface ImageExifConfig {
  base64Data: string;
  exifData: ImageExifData;
  canvas: HTMLCanvasElement;
}

export const downscaleImage = async (
  base64Data: string,
  config: ImageConfig,
  imageExifData: ImageExifData,
): Promise<{ base64Data: string; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { width: originalWidth, height: originalHeight } = img;
      let newWidth = originalWidth;
      let newHeight = originalHeight;

      // Calculate new dimensions while maintaining aspect ratio
      if (
        originalWidth > config.maxWidth ||
        originalHeight > config.maxHeight
      ) {
        const widthRatio = config.maxWidth / originalWidth;
        const heightRatio = config.maxHeight / originalHeight;
        const ratio = Math.min(widthRatio, heightRatio);

        newWidth = Math.floor(originalWidth * ratio);
        newHeight = Math.floor(originalHeight * ratio);
      }

      // Create canvas for resizing
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Draw image with new dimensions
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convert back to base64
      const quality = config.quality || ImageUtils.JPEG_COMPRESSION;
      const base64String = canvas.toDataURL('image/jpeg', quality);

      // Put the exif data back in the image
      const imageExifConfig: ImageExifConfig = {
        base64Data: base64String,
        exifData: imageExifData,
        canvas: canvas,
      };
      updateImageWithExifData(imageExifConfig);

      const base64Data = imageExifConfig.base64Data;

      resolve({
        base64Data,
        width: newWidth,
        height: newHeight,
      });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = `data:image/jpeg;base64,${base64Data}`;
  });
};

/**
 * Get the image's height and width and its exif data
 * @param resizeImageConfig
 */
export const getExifInfo = async (resizeImageConfig: ResizeImageConfig) => {
  const file = resizeImageConfig.file;
  const exifUtils = new ExifUtils();

  // Get an array buffer and then process the jpeg headers.
  const segments = await exifUtils.getHeaderSegmentsFromFile(file);

  // A valid JPEG will have basic segments like JFIF,
  let jpegHeader;
  let width;
  let height;
  if (segments.length > 0) {
    // Will need to be resized at some point. Grab the EXIF data.
    jpegHeader = {
      segments: segments,
      exif: null,
      exifSeg: -1,
    };
    const numSegs = jpegHeader.segments.length;

    // Find the EXIF segment.
    for (let i = 0; i < numSegs; i++) {
      const segData = jpegHeader.segments[i];
      if (segData[0] === 255 && segData[1] === 225) {
        // EXIF (APP1 marker)
        const dataView = new DataView(segData.buffer);
        // @ts-expect-error - parseExifData accepts a DataView and byte offset not in its type signature
        jpegHeader.exif = exifUtils.parseExifData(dataView, segData.byteOffset);
        jpegHeader.exifSeg = i; // If we need to update, this is the segment we'll want to update.
        break;
      }
    }

    var imageDimensions = exifUtils.imageSizeFromSegments(jpegHeader.segments);
    width = imageDimensions.width;
    height = imageDimensions.height;
  }

  // Check for width and height.
  if (isNaN(width) || isNaN(height)) {
    throw new Error(
      'Invalid JPEG detected. Could not parse width and height of image. Expected headers were not present.',
    );
  }

  // Create a response.
  return {
    width: width,
    height: height,
    exif: jpegHeader,
  };
};

/**
 * Update the image's orientation (if necessary) and put the exif data back in the base64Data property in ImageExifConfig
 * @param imageExifConfig
 */
export const updateImageWithExifData = (imageExifConfig: ImageExifConfig) => {
  const exifUtils = new ExifUtils();
  if (
    imageExifConfig.exifData.exif !== null &&
    imageExifConfig.exifData.exif.exif !== null
  ) {
    // set orientation, if necessary
    setExifOrientation(imageExifConfig, exifUtils);
  }
  if (imageExifConfig.exifData.exif !== null) {
    // Add EXIF data if we have it
    setExifData(imageExifConfig, exifUtils);
  }
};

/**
 * Called to set check the orientation flag in the EXIF data and rotate the image if necessary.
 * @param imageExifConfig
 * @param exifUtils
 */
const setExifOrientation = (
  imageExifConfig: ImageExifConfig,
  exifUtils: ExifUtils,
) => {
  /*
    0x0112 / 274 is the EXIF "Orientation" flag. These are the possible settings:

    1 = Horizontal (normal)
    2 = Mirror horizontal
    3 = Rotate 180
    4 = Mirror vertical
    5 = Mirror horizontal and rotate 270 CW
    6 = Rotate 90 CW
    7 = Mirror horizontal and rotate 90 CW
    8 = Rotate 270 CW
  */
  const orientation = exifUtils.getImageOrientationFromExifList(
    imageExifConfig.exifData.exif.exif,
  );
  if (1 !== orientation) {
    // TODO: Check to see if the commented out logic here is still necessary for today's browsers.
    // Rotate the canvas if auto rotate is not supported.
    // if (!isAutoRotateImageSupported) {
    //   // Set this image to the image manipulation control.
    //   const imageManipulation = new ImageManipulation()
    //   switch (orientation) {
    //     case exifUtils.ORIENTATION.BOTTOM_RIGHT: // 3
    //       imageManipulation.rotateCanvas(this.resizeCanvas, 180)
    //       break
    //     case exifUtils.ORIENTATION.RIGHT_TOP: // 6
    //       imageManipulation.rotateCanvas(this.resizeCanvas, 90)
    //       break
    //     case exifUtils.ORIENTATION.LEFT_BOTTOM: // 8
    //       imageManipulation.rotateCanvas(this.resizeCanvas, 270)
    //       break
    //     default:
    //       break
    //   }
    // }

    // Update the EXIF now.
    exifUtils.setImageOrientation(
      imageExifConfig.exifData.exif.segments,
      imageExifConfig.exifData.exif.exif,
      imageExifConfig.exifData.exif.exifSeg,
      exifUtils.ORIENTATION.TOP_LEFT,
    );
  }
};

/**
 * Called to set the exif data back into the final jpeg. Will check if there is thumbnail
 * markers and set the pointer to the data to 0 and it's length to 0 to fix an issue on the server.
 * @param imageExifConfig
 * @param exifUtils
 */
const setExifData = (
  imageExifConfig: ImageExifConfig,
  exifUtils: ExifUtils,
) => {
  // Is there exif data?
  if (
    null != imageExifConfig.exifData.exif &&
    null != imageExifConfig.exifData.exif.exif
  ) {
    // Clear the thumbnail data.
    exifUtils.clearThumbnailData(
      imageExifConfig.exifData.exif.segments,
      imageExifConfig.exifData.exif.exif,
      imageExifConfig.exifData.exif.exifSeg,
    );
  }

  // If there are JPEG header segments, write them back.
  if (
    null != imageExifConfig?.exifData?.exif.segments &&
    imageExifConfig.exifData.exif.segments.length > 0
  ) {
    // Call function to insert EXIF/IPTC and then convey array back to a dataUri.
    imageExifConfig.base64Data = exifUtils.arrayToJpegDataUri(
      exifUtils.insertExifIntoDataUri(
        imageExifConfig.base64Data,
        imageExifConfig.exifData.exif.segments,
      ),
    );
  }
};

/**
 * Return the blob of a given base64 data
 * @param dataURI the base64 data string of an image
 */
export const dataURItoBlob = (dataURI: any) => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  return new Blob([ab], { type: mimeString });
};
